const childBatches = (count: number): string => `${count} child ${count === 1 ? 'batch' : 'batches'}`;
const accountsRecording = (count: number): string => `${count} ${count === 1 ? 'account' : 'accounts'} recording`;
const batchCount = (count: number): string => `${count} ${count === 1 ? 'batch' : 'batches'}`;
const splitCount = (count: number): string => `${count} ${count === 1 ? 'split' : 'splits'}`;

const MINUTES_PER_HOUR = 60;

export const strings = {
  nav: 'Batch Tracking',
  common: { retry: 'Retry', back: 'Back', loadMore: 'Load more', none: '(none)', deleted: 'Deleted', cancel: 'Cancel' },

  scan: { label: 'Scan a label or type its code', resolving: 'Reading the label…' },

  batches: {
    loading: 'Loading batches…',
    loadError: "Couldn't load batches.",
    emptyFiltered: 'No batches match these filters.',
    empty: 'No batches yet. A batch is created when its label is first scanned, or added by hand on Home.',
    list: 'Batches',
    childCount: childBatches,
    facts: {
      qty: 'Qty',
      balance: 'Balance',
      children: 'Children',
      created: 'Created',
      supplierRef: 'Supplier reference'
    },
    tree: {
      list: 'Batches made from this batch',
      childList: (id: string) => `Batches made from ${id}`,
      showChildren: (args: { count: number; id: string }) => `Show ${childBatches(args.count)} of ${args.id}`,
      showAnyChildren: (id: string) => `Show child batches of ${id}`,
      loadingChildren: 'Loading child batches…',
      childrenLoadError: "Couldn't load the child batches."
    }
  },

  accounts: {
    nav: 'Accounts',
    records: 'Records',
    views: 'Batch Tracking views',
    subtitle: (kitchen: string) => `Accounts that recorded batches in ${kitchen} Kitchen`,
    today: 'Today',
    week: '7 days',
    month: '30 days',
    custom: 'Custom',
    dateRange: 'Date range',
    rangeLabel: (from: string, to: string) => `${from} – ${to}`,
    picker: {
      title: 'Custom range',
      from: 'From',
      to: 'To',
      apply: 'Apply',
      help: (days?: number) =>
        days === undefined ? 'Up to 31 days.' : `Up to 31 days. ${days} ${days === 1 ? 'day' : 'days'} selected.`
    },
    loading: 'Loading accounts…',
    loadError: "Couldn't load the accounts.",
    loadErrorHint: 'Check the connection and try again.',
    stale: (time: string) => `Couldn't refresh. Showing what was read at ${time}.`,
    staleAction: 'Try again',
    updated: (time: string) => `Updated ${time}`,
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    summaryLabel: 'Summary',
    kpi: {
      accounts: 'Accounts recording',
      batches: 'Batches recorded',
      deleted: 'Deleted',
      plusSplits: (count: number) => `+ ${splitCount(count)}`,
      ofCreated: (count: number) => `of ${count}`,
      batchesFoot: (live: number) => `By accounts · ${live} still live`,
      handheldFoot: (deleted: number) => `${deleted} deleted · not an account`,
      noAccounts: 'No account has recorded yet'
    },
    countNote: 'Deleted batches are included in the counts.',
    homeCard: (args: { accounts: number; batches: number }) =>
      `${accountsRecording(args.accounts)} today · ${batchCount(args.batches)}`,
    homeCardTitle: 'Accounts recording today',
    homeMeta: (last: string, updated: string) => `Last recorded ${last} · Updated ${updated}`,
    empty: 'No account recorded a batch in this window.',
    emptyHint: 'An account shows up here as soon as it records its first batch. This page checks again every minute.',
    table: {
      label: 'Accounts recording',
      account: 'Account',
      batches: 'Batches',
      splits: 'Splits',
      live: 'Live',
      deleted: 'Deleted',
      lastRecorded: 'Last recorded',
      expand: 'Show batches',
      ago: (minutes: number) => {
        if (minutes < 1) {
          return 'just now';
        }
        return minutes < MINUTES_PER_HOUR ? `${minutes} min ago` : `${Math.floor(minutes / MINUTES_PER_HOUR)} h ago`;
      },
      meta: (args: { last: string; splits: number; deleted: number }) =>
        `${args.last} · ${splitCount(args.splits)} · ${args.deleted} deleted`
    },
    drilldown: {
      title: (label: string) => `Batches recorded by ${label}`,
      loading: 'Loading batches…',
      loadError: "Couldn't load this account's batches.",
      empty: 'No batches.'
    },
    handhelds: { title: 'Handheld registrations' },
    trend: {
      hour: 'Batches recorded per hour',
      day: 'Batches recorded per day',
      accounts: 'Accounts',
      handhelds: 'Handhelds',
      total: 'Total',
      now: 'Now',
      busiestHour: (name: string, count: number) => `Busiest hour ${name} · ${batchCount(count)}`,
      busiestDay: (name: string, count: number) => `Busiest day ${name} · ${batchCount(count)}`,
      idle: 'Kitchen time, 00:00 to 23:00',
      emptyToday: 'No batches recorded yet today',
      emptyRange: 'No batches recorded in this window',
      emptyHint: 'Bars fill in as batches are recorded.',
      bucket: (args: { name: string; accounts: number; handhelds: number }) =>
        `${args.name}: ${args.accounts} by accounts, ${args.handhelds} by handhelds`
    }
  },

  filters: {
    button: (count: number) => (count > 0 ? `Filters (${count})` : 'Filters'),
    dateRange: 'Date range',
    item: 'Item',
    clear: 'Clear filters',
    clearAll: 'Clear all',
    reset: 'Reset',
    apply: 'Apply',
    chip: (args: { label: string; value: string }) => `${args.label}: ${args.value}`,
    dateChip: (args: { from: string; to: string }) => `Dates: ${args.from} – ${args.to}`
  },

  batch: {
    loading: 'Loading batch…',
    loadError: "Couldn't load this batch.",
    invalidId: "That ID doesn't look right. Check the address and try again.",
    deleted: (at: string) => `Deleted ${at}.`,
    noReference: 'No reference',
    children: 'Child batches',
    facts: {
      printed: 'Label printed',
      madeFrom: 'Made from',
      parent: 'Parent batch'
    }
  },

  delete: {
    action: 'Delete batch',
    title: (id: string) => `Delete batch ${id}?`,
    body: 'The batches made from it are deleted too. This cannot be undone.',
    confirm: 'Delete',
    done: 'Batch deleted.'
  },

  errors: {
    unauthorized: "You don't have access to do that.",
    unreadableLabel: "That label can't be read. Scan its QR code again, or type its label code.",
    labelConflict: "This label doesn't match what is already registered under its code. Check that it is the right label.",
    labelNotFound: (id = 'That label') => `${id} isn't registered. A label is registered by the handheld that printed it.`,
    batchNotFound: (id = 'That batch') => `${id} isn't registered in this kitchen.`,
    itemNotFound: "The item on this label isn't in this kitchen's list. Check the label.",
    unsupportedUnit: "This item isn't measured in g, ml or pieces, so it can't be tracked.",
    tooLarge: 'That request was too large. Narrow the filters and try again.',
    notCreator: 'Only the person who created this batch can change it.',
    editWindowClosed: 'The 15 minutes to change this batch are over.',
    batchDeleted: "This batch is deleted, so it can't be changed.",
    hasChildren: "Batches were already made from this one, so it can't be changed.",
    manualLabelConflict: "Couldn't save. Try again.",
    manualItemNotFound: "That item isn't in this kitchen's list. Pick another.",
    manualBatchNotFound: "This batch isn't there any more.",
    validation: "That can't be saved. Check the details and try again.",
    conflict: 'Another change was in progress. Try again.',
    generic: 'Something went wrong. Please try again.'
  }
} as const;
